import { useState, useEffect, useCallback } from 'react';
import { Gift, RefreshCw, Loader2, ExternalLink, FileDown } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';

const STATUS = {
  pending: { label: 'In attesa di pagamento', cls: 'bg-amber-100 text-amber-800' },
  paid: { label: 'Pagato · da usare', cls: 'bg-emerald-100 text-emerald-800' },
  redeemed: { label: 'Utilizzato', cls: 'bg-blue-100 text-blue-800' },
  cancelled: { label: 'Annullato', cls: 'bg-gray-200 text-gray-700' },
};

export default function AdminVouchers() {
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchVouchers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/vouchers');
      setVouchers(res.data);
    } catch (e) {
      toast.error('Errore nel caricamento dei buoni');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchVouchers(); }, [fetchVouchers]);

  const setStatus = async (v, status) => {
    try {
      await api.put(`/admin/vouchers/${v.id}`, { status });
      toast.success('Buono aggiornato');
      fetchVouchers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };

  const downloadPdf = async (v) => {
    try {
      const res = await api.get(`/admin/vouchers/${v.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error('Errore nella generazione del PDF');
    }
  };

  const paid = vouchers.filter((v) => v.status === 'paid');
  const total = vouchers.filter((v) => ['paid', 'redeemed'].includes(v.status)).reduce((a, v) => a + v.amount, 0);

  return (
    <div data-testid="admin-vouchers-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl mb-2 flex items-center gap-2"><Gift size={26} /> Buoni Regalo</h1>
          <p className="text-muted-foreground">Buoni acquistati dal sito (pagina /buono-regalo)</p>
        </div>
        <Button variant="outline" onClick={fetchVouchers} data-testid="vouchers-refresh-btn"><RefreshCw size={16} className="mr-2" />Aggiorna</Button>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-8">
        <Card><CardContent className="pt-4 pb-4"><p className="text-sm text-muted-foreground">Venduti</p><p className="text-2xl font-bold" data-testid="vouchers-sold-count">{vouchers.filter((v) => ['paid', 'redeemed'].includes(v.status)).length}</p></CardContent></Card>
        <Card><CardContent className="pt-4 pb-4"><p className="text-sm text-muted-foreground">Da utilizzare</p><p className="text-2xl font-bold" data-testid="vouchers-open-count">{paid.length}</p></CardContent></Card>
        <Card><CardContent className="pt-4 pb-4"><p className="text-sm text-muted-foreground">Incasso buoni</p><p className="text-2xl font-bold" data-testid="vouchers-total">€ {total.toFixed(0)}</p></CardContent></Card>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin" size={32} /></div>
      ) : vouchers.length === 0 ? (
        <Card><CardContent className="py-16 text-center text-muted-foreground">Nessun buono regalo ancora. <a href="/buono-regalo" target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-1">Vedi la pagina pubblica <ExternalLink size={14} /></a></CardContent></Card>
      ) : (
        <div className="space-y-3" data-testid="vouchers-list">
          {vouchers.map((v) => (
            <Card key={v.id} data-testid={`voucher-row-${v.id}`}>
              <CardContent className="pt-4 pb-4 flex flex-wrap items-center gap-4">
                <div className="min-w-[160px]">
                  <p className="font-mono font-bold tracking-wider">{v.code || '—'}</p>
                  <p className="text-xs text-muted-foreground">{new Date(v.created_at).toLocaleDateString('it-IT')}</p>
                </div>
                <div className="flex-1 min-w-[220px]">
                  <p className="text-sm"><span className="text-muted-foreground">Da</span> {v.buyer_name} <span className="text-muted-foreground">({v.buyer_email})</span></p>
                  <p className="text-sm"><span className="text-muted-foreground">Per</span> {v.recipient_name}{v.recipient_email ? ` (${v.recipient_email})` : ''}</p>
                  {v.message && <p className="text-xs italic text-muted-foreground mt-1">“{v.message}”</p>}
                  {v.redeemed_booking_code && <p className="text-xs mt-1">Usato nella prenotazione <strong>{v.redeemed_booking_code}</strong></p>}
                </div>
                <div className="text-right">
                  <p className="text-xl font-bold">€ {Number(v.amount).toFixed(0)}</p>
                  <p className="text-xs text-muted-foreground">{v.package}</p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <Badge className={STATUS[v.status]?.cls || ''}>{STATUS[v.status]?.label || v.status}</Badge>
                  <Button size="sm" variant="outline" onClick={() => downloadPdf(v)} data-testid={`voucher-pdf-${v.id}`}><FileDown size={14} className="mr-1" /> PDF</Button>
                  {v.status === 'paid' && <Button size="sm" variant="ghost" onClick={() => setStatus(v, 'redeemed')} data-testid={`voucher-redeem-${v.id}`}>Segna come usato</Button>}
                  {v.status === 'redeemed' && <Button size="sm" variant="ghost" onClick={() => setStatus(v, 'paid')}>Riattiva</Button>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
